import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import {
	getExportDetailsSummary,
	type ExportDetails,
} from "@/components/export-details/export-details-utils"
import { ExportDetailsView } from "@/components/export-details/ExportDetailsView"
import { replaceDialog } from "@/store/dialogs-state"
import { Sentry } from "@/telemetry/sentry"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"
import { isStorageQuotaError } from "@/utils/error"
import { getProductName } from "@/utils/product-name"
import { createElement } from "react"
import toast from "react-hot-toast"
import { logPhotoError } from "../photo/photo-error"

type PhotoExportFlow = "export" | "publish"
export type PhotoExportStorageError = true | "maybe"

interface ExportFlowStorageErrorConfig {
	maybeTrackingViewName: string
	trueTrackingViewName: string
	userConfirmedReportMessage: string
	incompleteTitle: string
}

const exportFlowStorageErrorConfig: Record<PhotoExportFlow, ExportFlowStorageErrorConfig> = {
	export: {
		maybeTrackingViewName: "export_maybe_storage_quota_exceeded",
		trueTrackingViewName: "export_storage_quota_exceeded",
		userConfirmedReportMessage:
			"NotReadableError encountered during export, user confirms with enough space",
		incompleteTitle: "Export Incomplete",
	},
	publish: {
		maybeTrackingViewName: "publish_maybe_storage_quota_exceeded",
		trueTrackingViewName: "publish_storage_quota_exceeded",
		userConfirmedReportMessage:
			"NotReadableError encountered during publish, user confirms with enough space",
		incompleteTitle: "Publish to Pixieset Incomplete",
	},
}

const buildStorageErrorMessage = (
	isConfirmedStorageError: boolean,
	storageErrorDetails?: ExportDetails,
) => {
	const reason = isConfirmedStorageError
		? "This stopped because your device ran out of available storage."
		: `This stopped because ${getProductName()} could not read or write one of the files. This can happen when disk space is low or the system ran out of memory.`
	const nextStep = isConfirmedStorageError
		? "Free up disk space and try again."
		: "Please check available storage and close other applications, then try again."

	if (!storageErrorDetails) {
		return `${reason} ${nextStep}`
	}

	return `${reason} ${getExportDetailsSummary(storageErrorDetails)} ${nextStep}`
}

export function showPhotoExportStorageErrorDialog(options: {
	flow: PhotoExportFlow
	storageError: PhotoExportStorageError
	retryHandler?: () => void
	storageErrorDetails?: ExportDetails
}) {
	const { flow, storageError, retryHandler, storageErrorDetails } = options
	const config = exportFlowStorageErrorConfig[flow]
	const embeddedDetails = storageErrorDetails
		? createElement(ExportDetailsView, { details: storageErrorDetails })
		: undefined

	switch (storageError) {
		case "maybe":
			replaceDialog(ConfirmDialog, {
				title: config.incompleteTitle,
				message: buildStorageErrorMessage(false, storageErrorDetails),
				primary: retryHandler
					? {
							title: i18n.t("common:action.retry"),
							handler: retryHandler,
						}
					: {
							title: i18n.t("common:action.ok"),
						},
				secondary: {
					title: i18n.t("store:photoExportError.errorHaveEnoughSpace"),
					handler: () => {
						Sentry.captureException(new Error(config.userConfirmedReportMessage))
						toast.success(i18n.t("components:photoExportError.errorReportSentWeAre"))
					},
				},
				embeddedComponent: embeddedDetails,
				trackingViewName: config.maybeTrackingViewName,
				canClose: Boolean(retryHandler),
			})
			break
		case true:
			replaceDialog(ConfirmDialog, {
				title: config.incompleteTitle,
				message: buildStorageErrorMessage(true, storageErrorDetails),
				primary: {
					title: retryHandler ? "Retry" : "OK",
					handler: retryHandler,
				},
				secondary: retryHandler ? { title: i18n.t("common:action.ok") } : undefined,
				embeddedComponent: embeddedDetails,
				trackingViewName: config.trueTrackingViewName,
			})
			break
	}
}

export function handlePhotoExportError(options: {
	error: unknown
	photoId: string
	flow: PhotoExportFlow
	onStorageError: (storageError: PhotoExportStorageError) => void
}) {
	const { error, photoId, flow, onStorageError } = options
	const isStorageError = isStorageQuotaError(error)

	void logPhotoError(error, {
		photoId,
		source: flow,
		logStorage: isStorageError !== false,
	})

	if (isStorageError === false) {
		return false
	}

	const flowLabel = flow.charAt(0).toUpperCase() + flow.slice(1)
	console.error(`[${flowLabel} Error]`, error)

	onStorageError(isStorageError)
	return true
}
